import type { ChapterContent, FirstGameStep } from '../../types';

/**
 * Voidfall — docs/cursor content lane (supreme quality pack).
 * Source: docs/perfect-quality/02_NOWE_GRY_PELNE_INSTRUKCJE/11_VOIDFALL_PELNA_INSTRUKCJA.md
 * Spec: docs/cursor/01_MASTER + docs/cursor/games/VOIDFALL_IMPLEMENTATION_PROMPT.md
 * No expansions in local source → no expansion menu chapters.
 * Catalogue blockers: see ./blockers.ts — do not invent maps, cards, AI or Combat Reference values.
 * Stable chapter titles and firstGame step ids (1–7) must be preserved for search/TTS/progress.
 */
export const voidfallChapters: ChapterContent[] = [
  {
    title: '1. Charakter gry i granice tej instrukcji',
    content: [
      {
        type: 'warning',
        text: 'Ten podręcznik uczy struktury systemu Voidfall z lokalnego szkicu Perfect Quality. Status ready wymaga osobnego katalogu scenariuszy, frakcji, kart Skupienia, technologii, Agend, Kryzysów, priorytetów Voidborn oraz zastosowania aktualnej oficjalnej erraty. Nie dopowiadaj map, liczb startowych ani tabel walki z pamięci. [DO WERYFIKACJI]',
      },
      {
        type: 'heading',
        text: 'Co to za gra?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Voidfall łączy zarządzanie imperium w stylu euro z eksploracją, rozwojem technologii i deterministyczną walką 4X. Nie jest to „tylko budowanie gospodarki” ani „tylko wojna”: sektory, populacja, Gildie, floty, Agendy i wydarzenia Cykli tworzą jeden system, w którym decyzje ekonomiczne i militarne wzajemnie się blokują albo wzmacniają.',
      },
      {
        type: 'heading',
        text: 'Trzy tryby',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gra ma trzy tryby: rywalizacyjny, kooperacyjny i solo. Każdy scenariusz określa mapę, liczbę Cykli, specjalne zasady, Cele, konfigurację Voidborn i warunki końca. Scenariusze nie różnią się wyłącznie kształtem mapy — mogą zmieniać cele, zasady, Voidborn i cały setup.',
      },
      {
        type: 'heading',
        text: 'Przykład przy stole',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Anna i Bartek otwierają pudełko i chcą „zagrywać jak w poprzedniej 4X”. Zamiast tego wybierają konkretny scenariusz z aplikacji, sprawdzają tryb i dopiero wtedy rozkładają mapę według diagramu tego scenariusza. Gdyby użyli setupu „z pamięci” z innej partii, warunki zwycięstwa i Voidborn mogłyby być już nielegalne.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie Voidfall jak jednej stałej mapy z trzema Cyklami. Scenariusz jest źródłem prawdy dla setupu i końca gry.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: przygotowanie — rozdział 3; tryby — rozdziały 17–18; koniec Cyklu — rozdział 19. Katalogi komponentów i errata: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Voidfall to scenariuszowy euro-4X w trzech trybach. P: Czy ta instrukcja kataloguje wszystkie frakcje i karty? O: Nie — lokalny szkic uczy procedur systemu; katalogi są blockerami. P: Czy dodatek jest w menu? O: Lokalne źródło nie opisuje gotowych dodatków; nie wymyślaj rozdziałów dodatków.',
      },
    ],
  },
  {
    title: '2. Cel gry według trybu',
    content: [
      {
        type: 'heading',
        text: 'Czego nauczysz się w tej części',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tu ustalasz, po co w ogóle grasz. Bez jasnego celu łatwo budować „ładne imperium”, które nie spełnia warunków scenariusza albo przegrywa kooperację mimo silnej gospodarki.',
      },
      {
        type: 'heading',
        text: 'Tryb rywalizacyjny',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W trybie rywalizacyjnym zdobywasz Wpływ przez rozwój sektorów, realizację Agend, wydarzenia Cykli, walkę i kontrolę. Wygrywa największy wynik po trzecim Cyklu — o ile scenariusz nie zmienia liczby Cykli albo warunków końca.',
      },
      {
        type: 'heading',
        text: 'Kooperacja i solo',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W kooperacji i solo gracze muszą jednocześnie osiągnąć wspólny cel i uniknąć warunków porażki, takich jak nadmierna siła Voidborn, nieopanowane Kryzysy albo niewykonane wymagania scenariusza. Sam „wysoki Wpływ” nie wystarczy, jeśli spełni się dowolny warunek porażki.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W rywalizacji Celina kończy trzeci Cykl z najwyższym Wpływem i wygrywa. W tej samej mapie granej kooperacyjnie ta sama gospodarka nic nie znaczy, jeśli Kryzysy i Voidborn spełnią warunek porażki przed wspólnym celem — stół przegrywa razem.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: w kooperacji optymalizować indywidualny Wpływ jak w rywalizacji i ignorować wspólne warunki porażki.',
      },
      {
        type: 'tip',
        text: 'Wyjątek: konkretne Cele i warunki porażki pochodzą ze scenariusza — [DO WERYFIKACJI: katalog scenariuszy]. Odsyłacz: tryby szczegółowo w rozdziałach 17–18.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Rywalizacja: największy Wpływ po Cyklach. Koop/solo: wspólny cel i uniknięcie porażki. P: Czy zawsze są trzy Cykle? O: Domyślnie tak, ale scenariusz może zmieniać liczbę lub kolejność części. P: Czy solo ma ten sam cel co koop? O: Używa zbliżonych systemów; decyzje automy mają priorytety — rozdział 18.',
      },
    ],
  },
  {
    title: '3. Przygotowanie',
    content: [
      {
        type: 'heading',
        text: 'Dlaczego setup nie może być „z pamięci”',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Voidfall jest grą scenariuszową. Mapa, populacja, Gildie, floty, Skażenie, Voidborn, Fallen Houses, frakcje, Focus cards, zasoby, wydarzenia Cykli, Agendy i rynek technologii muszą wynikać z wybranego scenariusza i trybu. Nie wolno użyć setupu z innej partii „bo wygląda podobnie”.',
      },
      {
        type: 'heading',
        text: 'Kolejność przygotowania',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Wybierz tryb i scenariusz.',
          'Zbuduj mapę dokładnie według diagramu scenariusza.',
          'Umieść sektory, populację, Gildie, floty i znaczniki Skażenia.',
          'Przygotuj Voidborn, Fallen Houses i cele.',
          'Każdy wybiera frakcję i Dom, bierze plansze, technologie, floty, Focus cards i zasoby.',
          'Ustaw trzy wskaźniki zasobów: wartość produkcji, zapas i limity zgodnie z planszą.',
          'Przygotuj wydarzenia Cykli, Agendy i rynek technologii.',
          'Dla kooperacji/solo przygotuj Kryzysy, priorytety Voidborn i elementy automy.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dawid wybiera kooperację. Po zbudowaniu mapy nie zaczyna od razu pierwszej tury: najpierw układa Kryzysy, priorytety Voidborn i elementy automy. Dopiero gdy wskaźniki produkcji i zapasu stoją zgodnie z planszą frakcji, stół uznaje setup za gotowy.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pominięcie osobnych wskaźników produkcji i zapasu albo używanie liczb startowych z innego scenariusza. [DO WERYFIKACJI: wszystkie mapy i liczby startowe]',
      },
      {
        type: 'tip',
        text: 'Aplikacja musi mieć dane dla każdego scenariusza — bez tego setup nie jest kompletny. Odsyłacz: zasoby — rozdział 6; Voidborn — rozdział 12.',
      },
      {
        type: 'checklist',
        id: 'voidfall-setup-checklist',
        title: 'Kontrola przed startem',
        items: [
          'Tryb i scenariusz wybrane; mapa zgodna z diagramem tego scenariusza.',
          'Sektory mają populację, Gildie, floty i Skażenie według arkusza.',
          'Wskaźniki produkcji i zapasu ustawione osobno; limity z planszy frakcji.',
          'Wydarzenia Cykli, Agendy i rynek technologii przygotowane.',
          'Koop/solo: Kryzysy, priorytety Voidborn i automy gotowe.',
        ],
      },
      {
        type: 'tip',
        text: 'Schemat przygotowania: tryb i scenariusz → mapa → elementy sektorów → Voidborn i cele → frakcje oraz zasoby → wydarzenia, Agendy i technologie → w kooperacji lub solo dodatkowo Kryzysy i automy.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Setup idzie od scenariusza do frakcji i dopiero potem do rynku oraz automy. Nie istnieje uniwersalna „standardowa mapa” niezależna od scenariusza.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę rozłożyć mapę z poprzedniej partii? Nie. Budujesz wyłącznie według diagramu wybranego scenariusza. Liczby startowe z innego arkusza są nielegalne.',
      },
      {
        type: 'paragraph',
        text: 'Czy kooperacja dodaje kroki setupu? Tak. Po rynku technologii przygotowujesz Kryzysy, priorytety Voidborn i elementy automy, zanim padnie pierwsza karta Skupienia.',
      },
    ],
  },
  {
    title: '4. Trzy Cykle — przebieg partii',
    content: [
      {
        type: 'heading',
        text: 'Idea Cyklu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Partia trwa trzy Cykle, o ile scenariusz nie stanowi inaczej. Cykl to nie „runda euro”, tylko pełny blok: przygotowanie i wydarzenie, naprzemienne tury z kartami Skupienia, a na końcu ocena, produkcja, cele i porządkowanie. Dopiero po trzecim Cyklu robisz punktowanie końcowe właściwe dla trybu.',
      },
      {
        type: 'heading',
        text: 'Co zawiera każdy Cykl',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'przygotowanie i wydarzenie Cykli;',
          'naprzemienne tury graczy z kartami Skupienia;',
          'ocenę Cykli, produkcję, rozliczenie celów i porządkowanie.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Scenariusz może zmieniać nazwy lub kolejność części. Dlatego nie uczysz stołu „zawsze w tej samej kolejności z pamięci”, tylko sprawdzasz arkusz scenariusza przed pierwszym Cykiem.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ewa zaczyna Cykl 1 od wydarzenia Cykli, potem gracze naprzemiennie grają karty Skupienia. Dopiero gdy wszyscy zakończą tury Cyklu, stół przechodzi do oceny, produkcji i porządkowania — nie „dokłada jeszcze jednej tury ekonomicznej”, bo ktoś „zapomniał”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijanie wydarzenia Cykli albo mieszanie końca Cyklu z kolejną turą Skupienia.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: karty Skupienia — rozdział 5; wydarzenia — rozdział 15; koniec Cyklu — rozdział 19.',
      },
      {
        type: 'tip',
        text: 'Schemat Cyklu: wydarzenie i przygotowanie → naprzemienne tury Skupienia → ocena, produkcja, cele i porządkowanie. Po trzecim Cyklu następuje punktowanie końcowe właściwe dla trybu, o ile scenariusz nie stanowi inaczej.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Trzy Cykle dzielą partię na blok wydarzeń, tur Skupienia i rozliczenia. Cykl kończy się porządkowaniem, a nie „jeszcze jedną turą z pamięci”.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę zagrać czwarty Cykl „dla remisu”? Nie. Po trzecim Cyklu robisz punktowanie końcowe, chyba że konkretny scenariusz zmienia liczbę Cykli lub warunki końca.',
      },
      {
        type: 'paragraph',
        text: 'Czy nazwy faz są zawsze takie same? Niekoniecznie. Scenariusz może zmieniać nazwy lub kolejność części — sprawdzasz arkusz przed pierwszym Cykiem.',
      },
    ],
  },
  {
    title: '5. Karty Skupienia',
    content: [
      {
        type: 'heading',
        text: 'Co to jest Skupienie?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karta Skupienia (Focus) definiuje zestaw powiązanych działań w Twojej turze. Nie wybierasz „dowolnej akcji z listy uniwersalnej”: najpierw legalna karta z ręki, potem dozwolona liczba jej działań w legalnej kolejności, z opłaceniem każdego kosztu.',
      },
      {
        type: 'heading',
        text: 'Po co istnieje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skupienie wymusza temat tury: produkcja, rozwój, badania, ekspansja, inwazja, polityka i Agendy, handel albo oczyszczanie Skażenia. Dzięki temu planujesz kilka tur naprzód zamiast robić wszystko naraz.',
      },
      {
        type: 'heading',
        text: 'Jak przebiega tura',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz legalną kartę z ręki;',
          'zagraj ją;',
          'wykonaj dozwoloną liczbę jej działań w legalnej kolejności;',
          'opłać każdy koszt;',
          'rozpatrz akcje dodatkowe i zdolności;',
          'odłóż kartę w miejsce zagranych kart.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Tekst karty ma pierwszeństwo przed regułą ogólną. Dokładna liczba wybieranych sekcji/akcji według aktualnego kompendium: [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Filip ma na ręce kartę rozwoju gospodarczego. Gra ją, wykonuje dozwolone działania Gildii i populacji, płaci każdy koszt z zapasu, rozpatruje ewentualną zdolność dodatkową i odkłada kartę na stos zagranych. Nie „dokłada” osobnej inwazji spoza karty, bo tekst na to nie pozwala.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wykonywanie akcji spoza zagranej karty albo pomijanie płatności przy każdym działaniu.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: koszty z zapasu — rozdział 6; katalogi Focus — [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Kiedy stosujesz tę procedurę',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Za każdym razem, gdy przypada Twoja tura w Cyklu i masz legalną kartę na ręce. Nie „dobierasz akcji z uniwersalnej listy stołu”: bez zagranej karty Skupienia nie ma tury w standardowym sensie systemu.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tura to legalna karta Skupienia, jej dozwolone działania w legalnej kolejności oraz opłacenie każdego kosztu. Tekst karty ma pierwszeństwo przed regułą ogólną.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy tekst karty bije ogólną regułę? Tak, w zakresie tej karty. Najpierw czytasz kartę, potem stosujesz ogólną procedurę tylko tam, gdzie karta milczy.',
      },
      {
        type: 'paragraph',
        text: 'Czy ta instrukcja podaje dokładną liczbę sekcji na każdej karcie? Nie. Dokładna liczba wybieranych sekcji lub akcji wymaga aktualnego kompendium. [DO WERYFIKACJI]',
      },
    ],
  },
  {
    title: '6. Zasoby: zapas i produkcja',
    content: [
      {
        type: 'heading',
        text: 'Dwa wskaźniki, nie jeden',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Każdy główny zasób ma wartość produkcji i aktualny zapas. To najczęstsze źródło katastrofalnych błędów u początkujących: mylenie „ile generuję” z „ile mogę wydać teraz”.',
      },
      {
        type: 'heading',
        text: 'Kiedy płacisz i skąd',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Płacąc koszt, zmniejszasz zapas, nie produkcję, chyba że efekt mówi wyraźnie inaczej. Oficjalne FAQ wyjaśnia, że koszty pobiera się ze stockpile widocznego na prawych tarczach zasobów. Produkcja jest aktualizowana przez populację, Gildie, technologie i sektory, a następnie zasila zapas w odpowiedniej fazie.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gosia ma produkcję 4 i zapas 2. Chce opłacić koszt 3. Nie może „obciąć produkcji do 1” zamiast zapasu. Musi mieć 3 w zapasie albo zmienić plan tury. Gdy później w fazie produkcji produkcja zasili zapas, dopiero wtedy zapas urośnie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: płacenie przez zmniejszenie produkcji zamiast zapasu.',
      },
      {
        type: 'tip',
        text: 'Wyjątek: efekt może kazać inaczej — wtedy tekst efektu ma pierwszeństwo. Odsyłacz: koniec Cyklu i produkcja — rozdział 19.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Produkcja mówi, ile zasobu generujesz w fazie; zapas mówi, ile możesz wydać teraz. Płacisz ze stockpile na prawych tarczach, nie z wartości produkcji.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy płacę z wartości produkcji? Standardowo nie. Zmniejszasz zapas. Efekt może kazać inaczej — wtedy tekst efektu wygrywa.',
      },
      {
        type: 'paragraph',
        text: 'Kiedy produkcja zasila zapas? W odpowiedniej fazie Cyklu, zwykle przy rozliczeniu końca Cyklu, a nie automatycznie przy każdej akcji w turze Skupienia.',
      },
    ],
  },
  {
    title: '7. Populacja i sektory',
    content: [
      {
        type: 'heading',
        text: 'Co jest w sektorze',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sektory to miejsca na mapie, w których trzymasz stan imperium. Zawierają planety, populację, miejsca Gildii, floty, obronę, Skażenie oraz potencjalne elementy Voidborn. Zwiększenie populacji odblokowuje produkcję i miejsca rozwoju — to nie jest kosmetyka, tylko bramka ekonomiczna.',
      },
      {
        type: 'heading',
        text: 'Skażony sektor a produkcja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skażony sektor nadal może produkować, ale nie można w nim zmieniać populacji i nie liczy się jako Czysty, dopóki nie zostanie oczyszczony. Nie traktuj Skażenia jak wyłączenia sektora z gry.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Hubert chce podnieść populację w sektorze ze Skażeniem, bo „i tak produkuje”. Reguła blokuje zmianę populacji. Może nadal korzystać z produkcji (o ile efekt nie mówi inaczej), ale najpierw musi zaplanować oczyszczenie albo rozwijać inny sektor.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie Skażonego sektora jako całkowicie wyłączonego z produkcji.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: Skażenie szczegółowo — rozdział 13; Gildie — rozdział 8.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Sektor łączy populację, Gildie, floty i stany. P: Czy Skażony sektor produkuje? O: Tak, chyba że konkretny efekt mówi inaczej. P: Czy mogę w nim zmieniać populację? O: Nie, dopóki jest Skażony.',
      },
    ],
  },
  {
    title: '8. Gildie',
    content: [
      {
        type: 'heading',
        text: 'Co to są Gildie?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gildie są głównymi strukturami ekonomicznymi sektora. Umieszczanie i ulepszanie Gildii to jeden z kluczowych sposobów podnoszenia produkcji i odblokowywania efektów — ale każdy typ Gildii ma osobne działanie, a katalog poziomów i ikon wymaga weryfikacji względem komponentów.',
      },
      {
        type: 'heading',
        text: 'Procedura umieszczenia lub ulepszenia',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz legalny slot;',
          'opłać koszt;',
          'sprawdź populację i wymagania;',
          'umieść poziom Gildii;',
          'zaktualizuj produkcję;',
          'rozpatrz natychmiastowy efekt.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Iga wybiera pusty legalny slot w sektorze z wystarczającą populacją, płaci koszt z zapasu, kładzie poziom Gildii, przesuwa wskaźnik produkcji i dopiero potem rozpatruje natychmiastowy efekt Gildii. Nie aktualizuje produkcji „na oko” po kilku turach.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pominięcie sprawdzenia populacji/slotu albo zapomnienie o aktualizacji produkcji zaraz po umieszczeniu.',
      },
      {
        type: 'tip',
        text: 'Katalog wszystkich poziomów i ikon Gildii: [DO WERYFIKACJI]. Koszty czytaj z kart i plansz — nie uśredniaj.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gildia = slot + koszt + wymagania + produkcja + efekt. P: Czy każdy typ działa tak samo? O: Nie — każdy typ ma osobne działanie. P: Czy ta instrukcja wymienia wszystkie ikony? O: Nie — to blocker katalogowy.',
      },
    ],
  },
  {
    title: '9. Technologie',
    content: [
      {
        type: 'heading',
        text: 'Rola technologii',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Technologie dają nowe akcje, modyfikują koszty, zwiększają siłę floty, wpływają na produkcję i punktowanie oraz zmieniają zasady frakcji. To warstwa „reguł specjalnych” Twojego Domu — ale zdobycie technologii nie oznacza automatycznego wykonania jej akcji.',
      },
      {
        type: 'heading',
        text: 'Kiedy uruchamiasz efekt',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Akcje technologii należy uruchamiać w podanym momencie: w turze, w fazie Cyklu albo w walce — zgodnie z tekstem. Trzymanie technologii na planszy nie jest równoznaczne z ciągłym wolnym działaniem.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Janek zdobywa technologię w turze badań. Nie wykonuje od razu jej akcji bojowej „bo już ją ma”. Czeka na moment wskazany na karcie — na przykład salwę w Bitwie albo fazę produkcji — i dopiero wtedy stosuje modyfikator.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: traktowanie zdobycia technologii jako natychmiastowego wykonania wszystkich jej akcji.',
      },
      {
        type: 'tip',
        text: 'Katalog technologii i tekstów: [DO WERYFIKACJI]. Odsyłacz: walka — rozdział 11.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Technologia zmienia reguły i akcje, ale timing jest na karcie. P: Czy zdobycie = natychmiastowa akcja? O: Nie. P: Czy technologia może zmienić punktowanie? O: Tak, jeśli tak stanowi.',
      },
    ],
  },
  {
    title: '10. Floty i ruch',
    content: [
      {
        type: 'heading',
        text: 'Co reprezentują floty',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Floty reprezentują siłę wojskową w sektorze. Figurki lub znaczniki mogą oznaczać liczbę, klasę i siłę. Ruch floty łączy mapę z walką: legalna trasa i zasięg decydują, czy w ogóle dojdziesz do konfliktu.',
      },
      {
        type: 'heading',
        text: 'Procedura ruchu',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz źródłowy sektor;',
          'określ legalną trasę;',
          'sprawdź zasięg i połączenia;',
          'przenieś dozwoloną część floty;',
          'jeśli w sektorze docelowym są wrogie siły, rozpocznij Inwazję/Bitwę.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Nie wolno przechodzić przez blokujące elementy bez zdolności. Limit floty możesz przekroczyć tylko w sytuacji wyraźnie dopuszczonej przez regułę lub erratę.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kasia wybiera sektor źródłowy, liczy legalne połączenia w zasięgu i przenosi część floty do sąsiedniego sektora z wrogimi siłami. Ponieważ nie ma zdolności omijania blokady, nie „przeskakuje” przez blokujący element — musi wybrać inną trasę albo inną akcję.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: ignorowanie blokad na trasie albo przenoszenie floty poza zasięg „bo mapa wygląda blisko”.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: pełna Bitwa — rozdział 11; wyjątki limitu przy Inwazji Voidborn — rozdział 12 i errata [DO WERYFIKACJI].',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ruch: źródło → trasa → zasięg → przeniesienie → ewentualna Bitwa. P: Czy wejście na wroga zawsze zaczyna Bitwę? O: Tak — Inwazję/Bitwę, gdy w docelowym są wrogie siły. P: Czy mogę iść przez blokadę? O: Tylko ze zdolnością.',
      },
    ],
  },
  {
    title: '11. Walka',
    content: [
      {
        type: 'warning',
        text: 'STATUS BITWY: BLOCKED dla pełnego rozstrzygnięcia liczbowego. W repozytorium nie ma lokalnego Combat Reference, PDF-ów rulebooka, player aidów ani erraty z tabelami siły i kolejnością typów flot. Ta sekcja uczy wyłącznie zweryfikowanej procedury z lokalnego szkicu Perfect Quality oraz oficjalnej reguły „absorpcja raz na krok salwy”. Nie wolno inventować wartości siły, kolejności salw typów, limitów absorpcji poza tą regułą, ani pełnego przykładu liczbowego. Bez papierowego Combat Reference i aktualnej erraty Mindclash nie rozstrzygniesz Bitwy wyłącznie z aplikacji. [DO WERYFIKACJI: Combat Reference]',
      },
      {
        type: 'heading',
        text: 'Czym jest Bitwa i po co istnieje',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bitwa to pełna, deterministyczna procedura rozstrzygania starcia flot w sektorze. Nie używa standardowego rzutu kością: wynik wynika z typów jednostek, ich siły, technologii, modyfikatorów, salw i absorpcji. Istnieje po to, by konflikt militarny był przewidywalny i uczciwy wobec składu floty oraz technologii — decyzje i efekty zmieniają wynik, a nie „szczęście z kości”.',
      },
      {
        type: 'heading',
        text: 'Kiedy Bitwa się zaczyna',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bitwa (Inwazja/Bitwa) zaczyna się, gdy po legalnym ruchu floty w sektorze docelowym znajdują się wrogie siły. Ruch ustalasz w rozdziale 10: źródło → legalna trasa → zasięg → przeniesienie. Wejście na pusty sektor wroga bez obecnych sił nie uruchamia tej procedury salw; wejście na sektor z wrogimi siłami — tak. Nie zastępujesz pełnej Bitwy regułami Skirmish, chyba że konkretny efekt karty, technologii albo scenariusza każe użyć Skirmish.',
      },
      {
        type: 'heading',
        text: 'Atakujący, obrońca i jednostki uczestniczące',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najpierw ustalasz strony: atakujący to strona, która właśnie wniosła flotę (lub której efekt wymusił starcie jako atak), obrońca to strona, której siły już stoją w sektorze. Do Bitwy wchodzą jednostki uczestniczące obecne w starciu zgodnie z regułami przygotowania sił — lokalne źródło wymaga ustalenia typów i siły jednostek, ale nie podaje, które typy wolno pomijać ani jak dzielić flotę poza tym, co wskaże Combat Reference i tekst efektu wywołującego starcie. Nie dopisuj „domyślnych” wyjątków z pamięci.',
      },
      {
        type: 'heading',
        text: 'Przygotowanie sił (force prep)',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przygotowanie sił to krok, w którym obie strony kładą na stół (lub w kalkulatorze) skład biorący udział w Bitwie: typy flot, ich liczbę oraz odczytaną z Combat Reference siłę. Dopiero po tym kroku stosujesz technologie i inne modyfikatory we wskazanych momentach. Bez Combat Reference nie znasz legalnej siły jednostek ani kolejności typów — dlatego pełne przygotowanie liczbowe pozostaje zablokowane w aplikacji.',
      },
      {
        type: 'heading',
        text: 'Kolejność typów flot i siła jednostek',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Kolejność typów flot (który typ strzela w której salwie) oraz tabela siły jednostek muszą pochodzić z aktualnego Combat Reference / kompendium. Lokalny szkic wprost odkłada te wartości do weryfikacji. Aplikacja nie podaje tu żadnych liczb ani kolejności „z pamięci”. [DO WERYFIKACJI: Combat Reference — tabela siły jednostek; kolejność typów flot / salw]',
      },
      {
        type: 'heading',
        text: 'Technologie i modyfikatory',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po ustaleniu typów i siły stosujesz technologie oraz inne modyfikatory we właściwym momencie wskazanym na komponencie — na przykład w konkretnej salwie albo w fazie przygotowania, jeśli tekst tak stanowi. Zdobycie technologii w turze badań nie uruchamia automatycznie jej efektu bojowego „bo już ją masz”: czekasz na moment z karty (rozdział 9). Nie inventuj wysokości bonusów siły ani specjalnych absorpcji spoza tekstu karty i Combat Reference.',
      },
      {
        type: 'heading',
        text: 'Salwy i absorpcja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Salwy to kolejne kroki wymiany obrażeń według kolejności typów z Combat Reference. W każdym kroku salwy rozpatujesz obrażenia tej salwy, a następnie absorpcję obrażeń należącą do tego kroku. Oficjalna errata: absorpcję danej salwy stosuje się tylko raz w jednym kroku salwy. Ponowne użycie tej samej absorpcji w tej samej salwie jest błędem. Inne limity absorpcji (które jednostki absorbują ile, czy limity kumulują się między salwami, jak łączą się z technologiami) wymagają Combat Reference i erraty — nie są w lokalnym źródle. [DO WERYFIKACJI: Combat Reference — pełne reguły absorpcji poza „raz na krok salwy”]',
      },
      {
        type: 'heading',
        text: 'Usuwanie strat',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po rozpatrzeniu salw i absorpcji usuwasz straty: jednostki zniszczone lub usunięte według reguł Combat Reference i tekstów efektów. Lokalne źródło wymaga kroku „usuń straty”, ale nie podaje tabeli „ile obrażeń niszczy który typ”. Nie zgaduj progów zniszczenia.',
      },
      {
        type: 'heading',
        text: 'Koniec Bitwy, wynik, przejęcie i nieudana inwazja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po stratach sprawdzasz wynik Bitwy. Lokalna procedura wymaga: sprawdź wynik → przejmij lub obroń sektor. Jeśli atakujący wygrywa zgodnie z Combat Reference / rulebookiem, przejmuje sektor według reguł scenariusza i komponentów. Jeśli obrońca utrzymuje sektor, inwazja kończy się niepowodzeniem — atakujący nie przejmuje kontroli; dalsze skutki (cofnięcie, pozostawienie resztek floty, natychmiastowe limity) bierzesz wyłącznie z Combat Reference, erraty i arkusza scenariusza. Lokalny szkic nie opisuje osobno „failed invasion” poza krokiem „przejmij lub obroń”. [DO WERYFIKACJI: Combat Reference / Rulebook — szczegół wyniku, nieudanej inwazji, wzajemnego zniszczenia i remisów]',
      },
      {
        type: 'heading',
        text: 'Wzajemne zniszczenie, remisy i brak rozstrzygnięcia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Lokalne źródło nie podaje reguł wzajemnego zniszczenia flot, remisu Bitwy ani sytuacji „brak rozstrzygnięcia”. Nie inventuj, kto „wygrywa przy zerowych flocie”, kto przejmuje sektor przy całkowitym wybiciu obu stron ani jak łamie się remis siły. Te przypadki graniczne zamyka wyłącznie oficjalny Combat Reference, Rulebook (sekcja Combat / Battle) oraz bieżąca errata Mindclash. Do czasu ich lokalnej weryfikacji aplikacja zostawia je jako blocker.',
      },
      {
        type: 'heading',
        text: 'Skażenie, nagrody i skutki scenariusza po Bitwie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po ustaleniu, kto przejął lub obronił sektor, rozpatujesz Skażenie, nagrody i skutki scenariusza. Skażenie to stan sektora (rozdział 13): ogranicza rozwój, blokuje zmianę populacji i status Czysty, ale nie wyłącza automatycznie produkcji. Czy dana Bitwa nakłada, usuwa albo zmienia Skażenie — wynika z efektu scenariusza, Karty wydarzenia, Agendy lub tekstu Inwazji, nie z „domyślnej kary za walkę” wymyślonej w aplikacji. Nagrody i skutki scenariusza (Wpływ, tokeny, cele, Kryzysy) bierzesz z arkusza scenariusza i komponentów wywołujących starcie. Timing: najpierw wynik i kontrola sektora, potem skutki wskazane przez scenariusz/efekt; nie przyznawaj sobie nagród „za samą walkę”, jeśli scenariusz tego nie daje.',
      },
      {
        type: 'heading',
        text: 'Bitwa a Skirmish',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skirmish to uproszczone rozstrzygnięcie wskazywane przez część efektów (rozdział 12). Inna procedura, inny zakres skutków. Nie wolno rozstrzygać pełnej Bitwy regułami Skirmish ani odwrotnie. Jeśli efekt mówi „Skirmish”, stosujesz Skirmish; jeśli ruch floty uruchamia Inwazję/Bitwę — pełną procedurę salw z Combat Reference.',
      },
      {
        type: 'heading',
        text: 'Pełny przebieg Bitwy (procedura)',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Potwierdź, że starcie to pełna Bitwa (nie Skirmish) — zwykle po ruchu floty na sektor z wrogimi siłami.',
          'Określ atakującego i obrońcę.',
          'Przygotuj siły uczestniczące: typy i liczba jednostek po obu stronach.',
          'Odczytaj z Combat Reference siłę jednostek oraz kolejność typów flot / salw. [BLOCKED w aplikacji bez Combat Reference]',
          'Zastosuj technologie i modyfikatory we wskazanych momentach.',
          'Rozpatrz salwy po kolei; w każdym kroku salwy zastosuj absorpcję co najwyżej raz (errata).',
          'Usuń straty według Combat Reference.',
          'Sprawdź wynik Bitwy (w tym przypadki graniczne z Combat Reference / Rulebook).',
          'Przejmij sektor albo uznaj obronę / nieudaną inwazję.',
          'Rozpatrz Skażenie, nagrody i skutki scenariusza wskazane przez komponenty.',
        ],
      },
      {
        type: 'heading',
        text: 'Przykład proceduralny end-to-end (bez inventowanych liczb)',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Leszek wykonuje ruch floty z sektora A do sektora B, w którym stoją siły Bartka. To pełna Bitwa, nie Skirmish. Leszek jest atakującym, Bartek obrońcą. Obaj kładą uczestniczące jednostki według typów obecnych w sektorze. Siłę każdej jednostki i kolejność salw odczytują z papierowego Combat Reference — aplikacja tych wartości nie podaje. Stosują technologie we wskazanych momentach (np. modyfikator Leszka tylko w salwie wskazanej na karcie). W pierwszej salwie Bartek chce użyć tej samej absorpcji dwa razy; errata na to nie pozwala — absorpcja tej salwy działa raz. Po wszystkich salwach usuwają straty według Combat Reference. Wynik (kto wygrywa, czy obie floty padają, czy jest remis) biorą z Combat Reference / Rulebook — lokalne źródło nie podaje tabeli wyniku. Jeśli Leszek wygrywa, przejmuje sektor B; jeśli nie, inwazja nie daje mu kontroli. Na końcu sprawdzają arkusz scenariusza i ewentualne efekty: czy Bitwa nakłada Skażenie, czy przyznaje nagrodę Wpływu, czy odpala skutek wydarzenia. Pełnego przykładu liczbowego (floty → suma siły → kolejność salw → modyfikatory → absorpcja → straty → zwycięzca → stan sektora → Skażenie/nagrody) nie da się tu domknąć bez Combat Reference — inventowanie liczb złamałoby standard treści.',
      },
      {
        type: 'heading',
        text: 'Czego brakuje do pełnej Bitwy (lista blockerów)',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'Tabela siły jednostek / typów flot — Combat Reference (player aid z pudełka Mindclash Voidfall; także aktualne kompendium / errata PDF na mindclashgames.com).',
          'Kolejność typów flot w salwach — Combat Reference.',
          'Pełne reguły absorpcji poza „raz na krok salwy” (limity, łączenie z technologiami) — Combat Reference + bieżąca errata.',
          'Progi / reguły usuwania strat per typ — Combat Reference.',
          'Reguły wyniku: zwycięzca, nieudana inwazja, wzajemne zniszczenie, remis / brak rozstrzygnięcia — Combat Reference + Rulebook (sekcja Combat / Battle).',
          'Konkretne nagradzanie i Skażenie po Bitwie per scenariusz — arkusz scenariusza + karty efektów.',
          'Pełny przykład liczbowy end-to-end — wymaga powyższych tabel; lokalnie niedostępny.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Materiał zamykający blocker: oficjalny Combat Reference (aid w pudełku) + Rulebook Mindclash Voidfall (sekcja Combat/Battle) + bieżąca errata/FAQ Mindclash. W repozytorium nie ma PDF-ów ani skanów tych stron — po dodaniu lokalnej, weryfikowalnej kopii wolno uzupełnić tabele i jeden pełny przykład liczbowy.',
      },
      {
        type: 'warning',
        text: 'Najczęstsze błędy: wielokrotna absorpcja w jednej salwie; rozstrzyganie pełnej Bitwy regułami Skirmish; inventowanie siły lub kolejności typów „z głowy”; pomijanie Skażenia/nagród scenariusza po Bitwie; uznanie tej sekcji za kompletną bez Combat Reference.',
      },
      {
        type: 'tip',
        text: 'Powiązania: ruch floty — rozdział 10; technologie w salwie — rozdział 9; Skirmish i Inwazja Voidborn — rozdział 12; Skażenie — rozdział 13; skutki scenariusza — rozdział 1–3 i arkusz. Schemat: strony → typy/siła (Combat Reference) → modyfikatory → salwy z jednorazową absorpcją → straty → wynik → sektor → Skażenie/nagrody. [DO WERYFIKACJI: Combat Reference]',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bitwa jest deterministyczną procedurą salw po Inwazji na sektor z wrogimi siłami. Aplikacja uczy kolejność kroków i erratę absorpcji raz na salwę, ale nie zastępuje Combat Reference. Nie rozstrzygasz pełnej Bitwy kością ani Skirmish — i nie uważasz tej sekcji za rulebook-free.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy walka jest losowa? Podstawowy system jest deterministyczny. Zmienne wynikają z decyzji, składu floty, technologii i efektów, nie z kości.',
      },
      {
        type: 'paragraph',
        text: 'Ile razy stosuję absorpcję w jednej salwie? Raz w jednym kroku salwy, zgodnie z oficjalną erratą. Ponowne użycie tej samej absorpcji w tej samej salwie jest błędem.',
      },
      {
        type: 'paragraph',
        text: 'Czy ta sekcja wystarczy do Bitwy bez papieru? Nie. Bez Combat Reference nie znasz siły, kolejności typów ani pełnego wyniku. Status: BLOCKED dla pełnej Bitwy liczbowej.',
      },
      {
        type: 'paragraph',
        text: 'Czym Bitwa różni się od Skirmish? Bitwa to pełna procedura salw po Inwazji; Skirmish to uproszczone rozstrzygnięcie tylko gdy efekt tak każe (rozdział 12).',
      },
    ],
  },
  {
    title: '12. Voidborn, Inwazja i Skirmish',
    content: [
      {
        type: 'heading',
        text: 'Kim jest Voidborn',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Voidborn to automatyczny przeciwnik systemowy. Działa zgodnie z priorytetami trybu i scenariusza — nie „jak wygodniej dla graczy”. W kooperacji i solo jego priorytety oraz Kryzysy są częścią przygotowania.',
      },
      {
        type: 'heading',
        text: 'Inwazja Voidborn',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podczas Inwazji Voidborn może czasowo przekroczyć zwykły limit mocy floty, jeśli aktualna errata tak stanowi. Po zakończeniu rozpatrz wymagane limity i straty. Nie generalizuj limitu bez erraty.',
      },
      {
        type: 'heading',
        text: 'Skirmish',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skirmish to uproszczone rozstrzygnięcie wykorzystywane przez część efektów. Nie należy utożsamiać go z pełną Bitwą — inna procedura, inny zakres skutków.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W kooperacji automy wskazuje Inwazję Voidborn. Marta chce wybrać cel „najłatwiejszy dla graczy”. Zamiast tego stosuje listę priorytetów i regułę remisu z scenariusza. Po starciu sprawdza, czy limity floty i straty wymagają korekty po tymczasowym przekroczeniu.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wybieranie priorytetu Voidborn na korzyść gracza albo używanie Skirmish zamiast pełnej Bitwy.',
      },
      {
        type: 'tip',
        text: 'Priorytety Voidborn i errata limitu: [DO WERYFIKACJI]. Odsyłacz: tryb kooperacyjny — rozdział 18.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Voidborn działa według twardych priorytetów. Skirmish nie jest pełną Bitwą. Limit floty przekraczasz tylko gdy reguła lub errata to dopuszcza.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę przekroczyć limit floty? Tylko gdy reguła lub errata wyraźnie dopuszcza, na przykład przy określonej Inwazji Voidborn. Potem rozliczasz limity i straty.',
      },
      {
        type: 'paragraph',
        text: 'Czy Skirmish kończy się przejęciem sektora jak Bitwa? Nie utożsamiaj procedur. Stosujesz regułę wskazaną przez efekt, który wywołał Skirmish.',
      },
    ],
  },
  {
    title: '13. Skażenie',
    content: [
      {
        type: 'heading',
        text: 'Co robi Skażenie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skażenie ogranicza rozwój sektora, blokuje zmianę populacji, może wpływać na punktowanie i status Czystego sektora, ale nie wyłącza automatycznie produkcji. To stan mapy, nie „wymazanie sektora”.',
      },
      {
        type: 'heading',
        text: 'Oczyszczanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Oczyszczanie wymaga konkretnej akcji i kosztu — zwykle z karty Skupienia lub zdolności. Usunięcie Skażenia może natychmiast odblokować dalsze możliwości (np. zmianę populacji), ale nie daje dodatkowej akcji, jeśli efekt tego nie mówi.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Norbert czyści Skażenie w sektorze. Od razu sektor przestaje blokować zmianę populacji i może znów liczyć się jako Czysty, jeśli spełnia pozostałe warunki. Norbert nie dostaje bonusowej drugiej akcji „za sprzątanie”, bo efekt oczyszczenia tego nie przyznaje.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: uznanie, że Skażenie wyłącza produkcję, albo przyznanie sobie darmowej dodatkowej akcji po oczyszczeniu.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: populacja — rozdział 7; FAQ ogólne powtarza tę regułę.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Skażenie blokuje rozwój populacji i status Czysty. Nie wyłącza automatycznie produkcji i nie daje darmowej ekstra akcji po oczyszczeniu.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy Skażony sektor produkuje? Tak, chyba że konkretny efekt mówi inaczej.',
      },
      {
        type: 'paragraph',
        text: 'Czy oczyszczenie daje dodatkową akcję? Tylko jeśli efekt oczyszczenia tak stanowi. Samo usunięcie Skażenia odblokowuje możliwości sektora, ale nie dokłada drugiej akcji znikąd.',
      },
    ],
  },
  {
    title: '14. Agendy i tokeny Handlu',
    content: [
      {
        type: 'heading',
        text: 'Czym są Agendy',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Agendy określają cele lub jednorazowe możliwości. Tekst Agendy może zmieniać zasadę ogólną — dlatego nie wolno „stosować Agendy jak uniwersalnej karty bonusowej” bez czytania ograniczeń czasu.',
      },
      {
        type: 'heading',
        text: 'Handel — kiedy i w jakiej kolejności',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Token Handlu to osobna możliwość ekonomiczna lub premia wskazana przez efekt. Zdobytej Agendy albo tokena Handlu nie wolno użyć w tej samej turze, w której został zdobyty. Wybór premii Handlu następuje zgodnie z kolejnością wskazaną przez jego efekt — nie „wybierasz najpierw to, co najwygodniejsze”, jeśli efekt ustala kolejność.',
      },
      {
        type: 'paragraph',
        text: 'Aplikacja i stół muszą pilnować stanu acquiredThisTurn: znacznik lub pamięć „zdobyte w tej turze” znika dopiero po zakończeniu tury, zanim wolno użyć efektu.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Ola zdobywa Agendę w środku tury Skupienia. Chce od razu użyć jej efektu, by domknąć punktowanie. Nie wolno — Agenda jest acquiredThisTurn. Może jej użyć w późniejszej turze, o ile nadal jest legalna. Ten sam timing dotyczy tokena Handlu zdobytego w tej turze.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: użycie nowej Agendy lub tokena Handlu w tej samej turze, w której został zdobyty.',
      },
      {
        type: 'tip',
        text: 'Katalog Agend i tokenów Handlu: [DO WERYFIKACJI]. Tekst Agendy może nadpisać ogólną regułę — czytaj kartę. Odsyłacz: punktowanie i cele — rozdziały 2 oraz 19.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Agenda i Handel dają cele lub premie, ale nie w turze zdobycia. Kolejność premii Handlu bierzesz z efektu, nie z wygody przy stole.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy mogę użyć tokena Handlu od razu po zdobyciu? Nie w tej samej turze. To ta sama zasada acquiredThisTurn co przy Agendzie.',
      },
      {
        type: 'paragraph',
        text: 'Czy Agenda może zmienić ogólną zasadę? Tak, w swoim zakresie. Tekst Agendy ma pierwszeństwo wobec ogólnej reguły, której dotyczy.',
      },
    ],
  },
  {
    title: '15. Wydarzenia Cykli',
    content: [
      {
        type: 'heading',
        text: 'Po co są wydarzenia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Na początku lub podczas Cykli wchodzą w życie wydarzenia. Mogą zmieniać punktowanie, dodawać zasady, modyfikować mapę, wzmacniać Voidborn albo tworzyć nowe cele. To nie ozdoba — pominięcie wydarzenia fałszuje cały Cykl.',
      },
      {
        type: 'heading',
        text: 'Co musi mieć każde wydarzenie w instrukcji cyfrowej',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'moment wejścia;',
          'okres działania;',
          'kolejność z Agendami i technologiami;',
          'koniec działania;',
          'przykład.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Lokalny szkic nie kataloguje konkretnych kart wydarzeń — każda musi być opisana przy scenariuszu. [DO WERYFIKACJI: katalog wydarzeń i scenariuszy]',
      },
      {
        type: 'heading',
        text: 'Przykład proceduralny',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Przed turami Skupienia w Cyklu 2 stół odsłania wydarzenie wzmacniające Voidborn. Gracze najpierw stosują moment wejścia wydarzenia, dopiero potem grają karty. Na końcu Cyklu sprawdzają, czy wydarzenie nadal działa, czy kończy się wraz z oceną.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijanie wydarzenia Cykli albo stosowanie go dopiero „gdy komuś pasuje”.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: struktura Cyklu — rozdział 4; koniec Cyklu — rozdział 19.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wydarzenie ma timing, okres i koniec — nie jest opcjonalnym wariantem. P: Czy wydarzenia tylko zmieniają mapę? O: Nie — też punktowanie, Voidborn, cele i zasady. P: Czy znam tu wszystkie teksty wydarzeń? O: Nie — blocker katalogowy.',
      },
    ],
  },
  {
    title: '16. Akcje ekonomiczne',
    content: [
      {
        type: 'heading',
        text: 'Jakie działania pojawiają się na kartach',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Typowe działania obejmują: zwiększenie populacji, rozbudowę Gildii, produkcję, handel, rozwój technologii, oczyszczenie sektora, przemieszczanie floty, Inwazję, zdobycie Agendy i zysk Wpływu. Lista uczy zakresu systemu — nie zastępuje tekstu konkretnej karty Skupienia.',
      },
      {
        type: 'heading',
        text: 'Koszty',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Dokładne koszty są na kartach i planszach. Nie wolno wprowadzać jednego „uśrednionego” kosztu ani zgadywać ikon. Każdą płatność bierz z zapasu zgodnie z rozdziałem 6, chyba że efekt mówi inaczej.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Paweł w turze Skupienia chce i zwiększyć populację, i rozbudować Gildię. Robi tylko to, co pozwala zagrana karta i jej dozwolona liczba działań, płacąc każdy koszt osobno z zapasu. Nie „uśrednia” dwóch kosztów w jedną płatność wymyśloną przy stole.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wymyślanie uśrednionych kosztów albo wykonywanie akcji spoza karty Skupienia.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: Skupienie — rozdział 5; Gildie — 8; floty — 10. Wartości drukowane: z komponentów, nie z tej instrukcji.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Akcje ekonomiczne i militarne mieszkają na kartach; koszty też. P: Czy jest uniwersalny cennik? O: Nie. P: Czy mogę zrobić każdą akcję z listy w każdej turze? O: Nie — tylko to, na co pozwala zagrane Skupienie.',
      },
    ],
  },
  {
    title: '17. Tryb rywalizacyjny',
    content: [
      {
        type: 'heading',
        text: 'Jak wygląda rywalizacja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W trybie rywalizacyjnym gracze kontrolują własne sektory, mogą wchodzić w konflikt, zdobywają indywidualny Wpływ, a Agendy i wydarzenia określają bieżące kierunki punktowania. Interakcje dyplomatyczne nie pozwalają na przekazywanie zasobów bez reguły — „umowa przy stole” nie tworzy legalnej płatności.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Radek obiecuje sąsiadowi „trzy zasoby za pokój”. Bez reguły albo efektu zezwalającego na przekazanie zasobów transakcja jest nielegalna. Konflikt rozwiązują legalnym ruchem floty i Bitwą, a Wpływ liczą indywidualnie.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: przekazywanie zasobów „po dyplomacji” bez reguły gry.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: cel rywalizacji — rozdział 2; walka — rozdział 11.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Rywalizacja = indywidualny Wpływ i legalny konflikt. P: Czy mogę oddać zasoby sojusznikowi? O: Nie bez reguły. P: Co wyznacza punktowanie w Cyklu? O: Agendy, wydarzenia, rozwój, walka i kontrola — według scenariusza.',
      },
    ],
  },
  {
    title: '18. Tryb kooperacyjny i solo',
    content: [
      {
        type: 'heading',
        text: 'Kooperacja przeciw Voidborn',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Gracze wspólnie mierzą się z Voidborn: rozpatrują Kryzysy, używają priorytetów automy, mogą mieć wspólny limit czasu lub cel i przegrywają po spełnieniu dowolnego warunku porażki scenariusza. Każda akcja automy musi mieć twardą listę priorytetów i regułę remisu.',
      },
      {
        type: 'heading',
        text: 'Solo',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Solo używa zbliżonych systemów do kooperacji, ale wszystkie decyzje wieloosobowe mają ustalony priorytet. Nie wybieraj wyniku korzystniejszego dla gracza, jeżeli istnieje reguła automy.',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'W solo Sylwia stoi przed remisem priorytetów Voidborn. Wybór A jest łatwiejszy dla niej, wybór B wynika z reguły remisu automy. Musi wybrać B. W kooperacji ta sama lista priorytetów chroni stół przed „głosowaniem na wygodniejszy cel”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: wybieranie priorytetu Voidborn na korzyść gracza albo pomijanie Kryzysów.',
      },
      {
        type: 'tip',
        text: 'Priorytety, Kryzysy i errata: [DO WERYFIKACJI]. Odsyłacz: Voidborn — rozdział 12.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koop/solo: wspólny cel, Kryzysy, twarde priorytety automy. P: Czy jeden warunek porażki kończy grę? O: Tak — po spełnieniu dowolnego warunku porażki scenariusza. P: Czy solo wolno „wybrać lepiej dla siebie” przy remisie automy? O: Nie, jeśli reguła automy istnieje.',
      },
    ],
  },
  {
    title: '19. Koniec Cyklu i punktowanie',
    content: [
      {
        type: 'heading',
        text: 'Co robisz na końcu Cyklu',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koniec Cyklu zamyka blok tur i aktualizuje stan partii przed następnym Cykiem. To moment produkcji, celów i Voidborn — nie „jeszcze jednej skróconej tury”.',
      },
      {
        type: 'heading',
        text: 'Procedura',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'rozpatrz aktualne wydarzenie i punktowanie;',
          'sprawdź cele;',
          'wykonaj produkcję/odświeżenie;',
          'przywróć karty i znaczniki;',
          'zaktualizuj Voidborn;',
          'przygotuj kolejny Cykl.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Po trzecim Cyklu wykonaj punktowanie końcowe i sprawdź warunek zwycięstwa właściwy dla trybu. W rywalizacji porównujesz Wpływ; w kooperacji i solo sprawdzasz wspólny sukces wobec warunków porażki.',
      },
      {
        type: 'heading',
        text: 'Remisy i tabele scenariusza',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Lokalny szkic nie podaje uniwersalnej reguły remisu Wpływu dla wszystkich scenariuszy. Jeśli dwóch graczy ma ten sam wynik, stosujesz regułę remisu z arkusza scenariusza albo z oficjalnego Rulebook/Glossary dla tej edycji — nie wymyślasz „dogrywki” przy stole. [DO WERYFIKACJI: tabele punktowania i remisy]',
      },
      {
        type: 'heading',
        text: 'Przykład',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Po turach Cyklu 1 Tomek chce od razu zagrać kolejną kartę Skupienia. Stół najpierw rozlicza wydarzenie i cele, wykonuje produkcję (produkcja zasila zapas), przywraca karty, aktualizuje Voidborn i dopiero wtedy otwiera Cykl 2. Po Cyklu 3 zamiast „Cyklu 4” robią punktowanie końcowe. Gdyby dwóch graczy miało ten sam Wpływ, sięgają do reguły remisu scenariusza zamiast głosować „kto bardziej zasłużył”.',
      },
      {
        type: 'warning',
        text: 'Typowy błąd: pomijanie produkcji i odświeżenia albo mylenie zasilania zapasu z płatnością z produkcji.',
      },
      {
        type: 'tip',
        text: 'Schemat końca Cyklu: wydarzenie i punkty → cele → produkcja lub odświeżenie → karty i znaczniki → Voidborn → kolejny Cykl albo punktowanie końcowe po trzecim. Szczegółowe tabele punktowania scenariusza: [DO WERYFIKACJI]. Odsyłacz: zasoby — rozdział 6; cel — rozdział 2.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Koniec Cyklu rozlicza, produkuje i porządkuje. Po trzecim Cyklu punktujesz finał według trybu i scenariusza, w tym ewentualny remis z arkusza — nie z pamięci.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy po Cyklu 3 gramy dalej? Nie. Robisz punktowanie końcowe, chyba że scenariusz mówi inaczej.',
      },
      {
        type: 'paragraph',
        text: 'Czy produkcja dzieje się w turze Skupienia? Zasilanie zapasu należy do odpowiedniej fazy Cyklu. W turze płacisz z zapasu; produkcja zasila zapas przy rozliczeniu, nie przy każdym koszcie akcji.',
      },
    ],
  },
  {
    title: '20. First Game i Quick Reference',
    content: [
      {
        type: 'heading',
        text: 'Ścieżka pierwszej partii',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pierwsza partia ma uczyć systemu, nie katalogu wyjątków. Użyj rekomendowanego scenariusza szkoleniowego, prostszych frakcji i mapy dokładnie z aplikacji. Guided powinien pokazać pierwszą kartę Skupienia. Pierwszą turę ekonomiczną przeprowadź bez skracania; pierwszą walkę rozpatrz z interaktywnym kalkulatorem salw. Przed końcem Cyklu pokaż różnicę między zapasem a produkcją. Nie dołączaj zaawansowanych Agend przed zrozumieniem podstaw.',
      },
      {
        type: 'heading',
        text: 'Quick Reference przy stole',
        level: 2,
      },
      {
        type: 'step-list',
        steps: [
          'wybierz kartę Skupienia;',
          'wykonaj dozwolone akcje;',
          'płać z zapasu;',
          'aktualizuj produkcję osobno;',
          'sprawdź legalność sektora;',
          'walka: modyfikatory → salwy → absorpcja → straty → wynik;',
          'nie używaj nowej Agendy/tokena Handlu w tej samej turze;',
          'Skażony sektor produkuje, ale blokuje zmianę populacji i status Czysty;',
          'po Cyklu: cele, produkcja, odświeżenie, Voidborn.',
        ],
      },
      {
        type: 'warning',
        text: 'Typowy błąd pierwszej partii: wrzucenie zaawansowanych Agend i pełnego katalogu frakcji zanim stół ogarnie zapas vs produkcję.',
      },
      {
        type: 'tip',
        text: 'Odsyłacz: Guided kroki poniżej eksportują tę ścieżkę. Szczegóły walki — rozdział 11.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'First Game upraszcza zakres; Quick Reference pilnuje kolejności. P: Czy szkoleniowy scenariusz jest w tej instrukcji nazwany? O: Lokalny szkic każe użyć rekomendowanego — konkretna nazwa: [DO WERYFIKACJI]. P: Co pokazać przed końcem Cyklu? O: Różnicę zapasu i produkcji.',
      },
    ],
  },
  {
    title: 'NAJCZĘSTSZE BŁĘDY',
    content: [
      {
        type: 'heading',
        text: 'Błędy, które psują partię',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Poniższe pomyłki powtarzają się przy Voidfall najczęściej. Przy każdym błędzie stosuj poprawną regułę z powiązanego rozdziału.',
      },
      {
        type: 'step-list',
        steps: [
          'Płacenie przez zmniejszenie produkcji — płać z zapasu (rozdział 6).',
          'Traktowanie Skażonego sektora jako całkowicie wyłączonego — produkcja może działać; blokowana jest zmiana populacji i status Czysty (rozdział 13).',
          'Użycie nowej Agendy w tej samej turze — acquiredThisTurn (rozdział 14).',
          'Wielokrotne stosowanie absorpcji w jednej salwie — raz na krok salwy (rozdział 11).',
          'Rozstrzyganie pełnej Bitwy regułami Skirmish — to osobne procedury (rozdział 12).',
          'Pomijanie wydarzenia Cykli — wydarzenie ma moment wejścia (rozdział 15).',
          'Używanie setupu innego scenariusza — mapa i liczby ze scenariusza (rozdział 3).',
          'Wybieranie priorytetu Voidborn na korzyść gracza — twarde priorytety automy (rozdział 18).',
        ],
      },
      {
        type: 'heading',
        text: 'Podsumowanie i mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Najdroższe błędy dotyczą zapasu, Skażenia, Agend, absorpcji i automy. P: Który błąd psuje kooperację najszybciej? O: Priorytety Voidborn „pod gracza” i pominięte Kryzysy. P: Który psuje gospodarkę? O: Płatność z produkcji.',
      },
    ],
  },
  {
    title: 'FAQ',
    content: [
      {
        type: 'heading',
        text: 'Jak korzystać z FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Poniższe odpowiedzi domykają spory proceduralne z lokalnego źródła. Nie zastępują katalogów kart ani Combat Reference. Gdy pytanie dotyczy konkretnej frakcji, technologii albo scenariusza, sięgasz do komponentu — nie zgadujesz tu wartości.',
      },
      {
        type: 'heading',
        text: 'Czy Skażony sektor produkuje?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tak, chyba że konkretny efekt mówi inaczej. W Skażonym sektorze nie możesz zmieniać populacji i sektor nie jest Czysty, dopóki go nie oczyścisz. Produkcja nie wyłącza się sama z faktu Skażenia.',
      },
      {
        type: 'heading',
        text: 'Czy płacę z wartości produkcji?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nie. Standardowo płacisz z zapasu (stockpile) widocznego na prawych tarczach zasobów. Produkcja zasila zapas w odpowiedniej fazie Cyklu. Efekt może kazać inaczej — wtedy tekst efektu ma pierwszeństwo.',
      },
      {
        type: 'heading',
        text: 'Czy mogę użyć tokena Handlu od razu po zdobyciu?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nie w tej samej turze. Zdobyty token Handlu oraz zdobyta Agenda mają status acquiredThisTurn i wolno je użyć dopiero w późniejszej turze, o ile nadal są legalne.',
      },
      {
        type: 'heading',
        text: 'Czy walka jest losowa?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Podstawowy system Bitwy jest deterministyczny. Zmienne wynikają z decyzji, składu floty, technologii i efektów. Nie rozstrzygasz pełnej Bitwy rzutem kości ani regułami Skirmish, chyba że efekt każe użyć Skirmish.',
      },
      {
        type: 'heading',
        text: 'Czy scenariusze różnią tylko mapą?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Nie. Scenariusz może zmieniać cele, zasady specjalne, konfigurację Voidborn, liczbę lub przebieg Cykli oraz cały setup. Mapa to tylko część prawdy scenariusza.',
      },
      {
        type: 'heading',
        text: 'Czy mogę przekroczyć limit floty?',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Tylko w sytuacji wyraźnie dopuszczonej przez regułę lub erratę, na przykład podczas określonej Inwazji Voidborn. Po takim wyjątku rozpatrz wymagane limity i straty. Nie generalizuj limitu „bo Voidborn”.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'FAQ trzyma się procedur: zapas kontra produkcja, Skażenie, timing Agend i Handlu, deterministyczna Bitwa oraz prymat scenariusza. Katalogi i tabele: rozdziały z [DO WERYFIKACJI] oraz plik blockers.',
      },
    ],
  },
  {
    title: 'GLOSSARY',
    content: [
      {
        type: 'heading',
        text: 'Słownik pojęć',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Poniższe hasła pomagają nie mylić pojęć przy stole. Każde odsyła do procedury w rozdziałach powyżej.',
      },
      {
        type: 'heading',
        text: 'Cykl',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Jedna z trzech głównych części partii. Obejmuje wydarzenie lub przygotowanie, tury Skupienia oraz rozliczenie z produkcją i porządkowaniem.',
      },
      {
        type: 'heading',
        text: 'Skupienie / Focus',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Karta określająca dozwolone działania Twojej tury. Nie jest uniwersalną listą „zrób cokolwiek”.',
      },
      {
        type: 'heading',
        text: 'Zapas',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Bieżąca ilość zasobu możliwa do wydania teraz. Standardowe koszty bierzesz z zapasu.',
      },
      {
        type: 'heading',
        text: 'Produkcja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Wartość generująca zasoby w odpowiedniej fazie Cyklu. Nie myl jej z zapasem przy płatnościach.',
      },
      {
        type: 'heading',
        text: 'Gildia',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Struktura gospodarcza w sektorze. Umieszczenie lub ulepszenie wymaga slotu, kosztu, wymagań populacji i aktualizacji produkcji.',
      },
      {
        type: 'heading',
        text: 'Skażenie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Stan sektora ograniczający rozwój: blokuje zmianę populacji i status Czysty, ale nie wyłącza automatycznie produkcji.',
      },
      {
        type: 'heading',
        text: 'Voidborn',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Automatyczny przeciwnik systemowy działający według priorytetów trybu i scenariusza, nie według wygody graczy.',
      },
      {
        type: 'heading',
        text: 'Agenda',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Cel albo specjalna możliwość. Tekst Agendy może zmieniać zasadę ogólną; nie używasz jej w turze zdobycia.',
      },
      {
        type: 'heading',
        text: 'Bitwa',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Pełna deterministyczna procedura salw po Inwazji na sektor z wrogimi siłami. Siła, kolejność typów i pełny wynik wymagają Combat Reference — rozdział 11 jest BLOCKED liczbowo.',
      },
      {
        type: 'heading',
        text: 'Absorpcja',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Redukcja obrażeń w kroku salwy. Oficjalna errata: daną absorpcję stosujesz tylko raz w jednym kroku salwy.',
      },
      {
        type: 'heading',
        text: 'Skirmish',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Uproszczone starcie wskazywane przez część efektów. Jest odmienne od pełnej Bitwy — nie mieszaj procedur.',
      },
      {
        type: 'heading',
        text: 'Podsumowanie',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Słownik rozróżnia zapas od produkcji, Bitwę od Skirmish oraz absorpcję (raz na salwę). Focus wyznacza turę; Cykl to więcej niż jedna tura. Pełna Bitwa liczbowa wymaga Combat Reference.',
      },
      {
        type: 'heading',
        text: 'Mikro-FAQ',
        level: 2,
      },
      {
        type: 'paragraph',
        text: 'Czy Focus oznacza dowolną akcję? Nie. Karta wyznacza dozwolone działania i ich liczbę według swojego tekstu oraz kompendium.',
      },
      {
        type: 'paragraph',
        text: 'Czy Cykl to pojedyncza tura? Nie. Cykl obejmuje wydarzenie, tury graczy i rozliczenie.',
      },
    ],
  },
];

export const voidfallFirstGame: FirstGameStep[] = [
  {
    id: 1,
    title: 'Scenariusz szkoleniowy',
    content: [
      'Wybierz rekomendowany scenariusz szkoleniowy i tryb.',
      'Nie używaj setupu z innej partii ani „z pamięci”.',
    ],
    why: 'Scenariusz ustala mapę, cele, Voidborn i warunki końca.',
    commonMistake: 'Rozkładanie mapy z poprzedniej sesji.',
    chapterIndex: 2,
  },
  {
    id: 2,
    title: 'Prostsze frakcje i mapa',
    content: [
      'Wybierz prostsze frakcje.',
      'Rozłóż mapę dokładnie według diagramu w aplikacji.',
    ],
    why: 'Zmniejsza przeciążenie wyjątkami frakcji przy pierwszej nauce systemu.',
    commonMistake: 'Dokładanie zaawansowanych frakcji i Agend od razu.',
    chapterIndex: 2,
  },
  {
    id: 3,
    title: 'Pierwsza karta Skupienia',
    content: [
      'Guided pokazuje pierwszą kartę Skupienia.',
      'Zagraj tylko legalne działania tej karty i opłać koszty z zapasu.',
    ],
    why: 'Uczy, że tura wynika z karty, nie z uniwersalnej listy akcji.',
    commonMistake: 'Wykonywanie akcji spoza zagranej karty.',
    chapterIndex: 4,
  },
  {
    id: 4,
    title: 'Pełna tura ekonomiczna',
    content: [
      'Przeprowadź pierwszą turę ekonomiczną bez skracania.',
      'Przy każdej płatności wskaż zapas, nie produkcję.',
    ],
    why: 'Utrwala różnicę zapasu i produkcji zanim wejdzie walka.',
    commonMistake: 'Płacenie przez obniżenie produkcji.',
    chapterIndex: 5,
  },
  {
    id: 5,
    title: 'Pierwsza walka z kalkulatorem',
    content: [
      'Rozpatrz pierwszą walkę z interaktywnym kalkulatorem salw.',
      'Zastosuj absorpcję tylko raz w kroku salwy.',
    ],
    why: 'Pokazuje deterministyczną Bitwę zamiast „rzutu kością z głowy”.',
    commonMistake: 'Używanie reguł Skirmish albo wielokrotnej absorpcji.',
    chapterIndex: 10,
  },
  {
    id: 6,
    title: 'Koniec Cyklu: zapas vs produkcja',
    content: [
      'Przed końcem Cyklu pokaż wskaźniki produkcji i zapasu.',
      'Wykonaj produkcję/odświeżenie według procedury końca Cyklu.',
    ],
    why: 'Zamyka pętlę ekonomiczną i przygotowuje kolejny Cykl.',
    commonMistake: 'Pomijanie wydarzenia Cykli albo produkcji na końcu Cyklu.',
    chapterIndex: 18,
  },
  {
    id: 7,
    title: 'Odłóż zaawansowane Agendy',
    content: [
      'Nie dołączaj zaawansowanych Agend, dopóki stół nie ogarnie podstaw.',
      'Gdy Agenda wejdzie, pilnuj acquiredThisTurn.',
    ],
    why: 'Agendy zmieniają punktowanie i timing — najpierw rdzeń systemu.',
    commonMistake: 'Użycie Agendy w turze zdobycia.',
    chapterIndex: 13,
  },
];
